import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2026-06-23 15:36:55 +0200 (Tue, 23 Jun 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163948 $
//  Purpose   : I18N related parts
// =============================================================================
// ---Third-party imports
import React from 'react';
import { matchSorter } from 'match-sorter';
import { Button, Form } from 'semantic-ui-react';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import { keys, sorted, uniq } from 'ldash';
import { HeaderSegment, TranslatedTable, XMenu } from 'uix/components';
import { useRouter, useSelector, useMemo, useActions, useState, useCallApi, useNotify } from 'uix/hooks';
import { usePersistentToggle, usePersistentButtonGroup } from 'uix/components/menu';
// ---Globals
const filterMethod = (key)=>(filter, rows)=>matchSorter(rows, filter.value, {
            keys: [
                key
            ]
        });
const cellKey = (lang, ident)=>`${lang}:${ident}`;
const collectTranslationIdents = (languages, ...sources)=>sorted(uniq(languages.flatMap((lang)=>sources.flatMap((source)=>keys(source[lang])))));
const EditableTranslationCell = ({ lang, ident, value, onChange, savingKey, sourceHint })=>{
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(value || '');
    const currentKey = cellKey(lang, ident);
    const isSaving = savingKey === '*' || savingKey === currentKey;
    const startEditing = (e)=>{
        e.stopPropagation();
        setText(value || '');
        setEditing(true);
    };
    const cancelEditing = (e)=>{
        e && e.stopPropagation();
        setEditing(false);
        setText(value || '');
    };
    const submitChange = (newValue)=>{
        if (!onChange) {
            setEditing(false);
            return;
        }
        const targetValue = newValue != null ? newValue : text;
        onChange({
            lang,
            ident,
            value: targetValue
        });
        setEditing(false);
    };
    const renderSourceHint = ()=>{
        if (!sourceHint) {
            return null;
        }
        const { label, title, origin } = sourceHint;
        return /*#__PURE__*/ _jsx("span", {
            className: `i18n-source-hint ${origin || ''}`,
            style: {
                float: 'right',
                marginLeft: '0.5em',
                fontSize: '0.75em',
                color: origin === 'overlay' ? '#a5673f' : '#767676'
            },
            title: title || label,
            children: label || title
        });
    };
    if (!editing) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "i18n-editable-cell",
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: value ? '' : 'muted',
                    children: value || /*#__PURE__*/ _jsx("i", {
                        children: "—"
                    })
                }),
                /*#__PURE__*/ _jsx(Button, {
                    icon: "edit",
                    size: "mini",
                    basic: true,
                    compact: true,
                    floated: "right",
                    onClick: startEditing,
                    title: "Edit translation (creates a DB overlay)"
                }),
                renderSourceHint()
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Form, {
        size: "small",
        onSubmit: (e)=>{
            e.preventDefault();
            submitChange(text);
        },
        children: [
            /*#__PURE__*/ _jsx(Form.Input, {
                autoFocus: true,
                value: text,
                onChange: (e, { value })=>setText(value || ''),
                disabled: isSaving
            }),
            /*#__PURE__*/ _jsxs(Form.Group, {
                inline: true,
                children: [
                    /*#__PURE__*/ _jsx(Form.Button, {
                        primary: true,
                        icon: "save",
                        content: "Save",
                        disabled: isSaving,
                        loading: isSaving,
                        type: "submit"
                    }),
                    /*#__PURE__*/ _jsx(Form.Button, {
                        negative: true,
                        icon: "trash",
                        content: "Delete",
                        disabled: isSaving || !(sourceHint?.origin == 'overlay'),
                        loading: isSaving && text === '',
                        type: "button",
                        onClick: ()=>submitChange('')
                    }),
                    /*#__PURE__*/ _jsx(Form.Button, {
                        basic: true,
                        icon: "cancel",
                        content: "Cancel",
                        type: "button",
                        disabled: isSaving,
                        onClick: cancelEditing
                    })
                ]
            })
        ]
    });
};
export const I18NTable = ({ flex, onOverlayChange, overlaySavingKey, sourceInfo })=>{
    const { request } = useRouter();
    let dconfig = useSelector(Sel.dconfig);
    let i18n = useSelector(Sel.i18n);
    let switchLanguage = useActions(Actions.switchLanguage);
    let reloadLanguage = useActions(Actions.reloadLanguage);
    let selectedLang = useSelector((root)=>root.meta.language);
    let [showAll, showAllToggle] = usePersistentToggle('showAll', {
        initialValue: false,
        icon: 'eye',
        content: 'Loaded',
        title: 'Show all loaded langues'
    });
    let [showOverlayRowsDB, showOverlayRowsDBToggle] = usePersistentToggle('showOverlayRowsDB', {
        initialValue: false,
        icon: 'filter',
        content: 'Modified in DB'
    });
    //~ let [showOverlayRowsFS, showOverlayRowsFSToggle] = usePersistentToggle('showOverlayRowsFS', {
    //~ initialValue: false,
    //~ icon: 'filter',
    //~ content: 'Modified on FS',
    //~ })
    let [overlayFilter, overlayFilterButtonGroup] = usePersistentButtonGroup('showOverlayFilter', {
        initialValue: 'all',
        buttons: [
            {
                name: 'all',
                icon: 'filter',
                content: 'Alle'
            },
            {
                name: 'db_fs',
                icon: 'filter',
                content: 'DB/FS'
            },
            {
                name: 'db',
                icon: 'filter',
                content: 'DB'
            },
            {
                name: 'fs',
                icon: 'filter',
                content: 'FS'
            }
        ]
    });
    let notify = useNotify();
    let { callApi: saveOverlay, calling } = useCallApi('i18n/saveOverlayValue');
    let [savingKey, setSavingKey] = useState(null);
    let dname = useSelector((root)=>root.meta.request.dname);
    let ddata = useSelector((root)=>root.config.ddata);
    let i18n_domain_raw = ddata[dname]?.i18n;
    let i18n_raw = useSelector((root)=>root.config.instance.i18n);
    //~ vl.pprint({i18n_domain_raw, ddata, dname, dn})
    if (!i18n_domain_raw) {
        return null;
    }
    let active_langs = keys(i18n_raw).filter((lang)=>showAll || lang == selectedLang);
    let defaultLang = dconfig.language.supported[0] || selectedLang;
    let rowLangs = useMemo(()=>showAll ? uniq([
            ...keys(i18n_domain_raw),
            ...keys(i18n_raw)
        ]) : uniq([
            defaultLang,
            selectedLang
        ]), [
        showAll,
        i18n_domain_raw,
        i18n_raw,
        defaultLang,
        selectedLang
    ]);
    const handleOverlayChange = ({ lang, ident, value })=>{
        const targetKey = cellKey(lang, ident);
        setSavingKey(targetKey);
        const finish = ()=>setSavingKey(null);
        if (onOverlayChange) {
            onOverlayChange({
                lang,
                ident,
                value
            });
            finish();
            return;
        }
        saveOverlay({
            lang,
            ident,
            value
        }, (response, error)=>{
            finish();
            if (error) {
                notify.error({
                    error
                });
            } else if (response && response.i18nsaved === true) {
                notify.success({
                    message: `Saved overlay for ${lang}/${ident}`
                });
                // update environment
                reloadLanguage({
                    language: lang
                });
            } else if (response && response.i18nsaved === false) {
                notify.warning({
                    message: 'Overlay update reported an error.'
                });
            } else {
                notify.info({
                    message: 'Overlay update completed.'
                });
            }
        });
    };
    const effectiveSavingKey = overlaySavingKey != null ? overlaySavingKey : savingKey || (calling ? '*' : null);
    const getSourceHint = (lang, ident)=>{
        if (!sourceInfo || !lang || !ident) {
            return null;
        }
        const langInfo = sourceInfo[lang];
        if (!langInfo) {
            return null;
        }
        const entry = langInfo[ident];
        if (!entry || !entry.source) {
            return null;
        }
        const origin = entry.origin || 'file';
        const sourceText = String(entry.source || '');
        const domain = entry.domain ? String(entry.domain) : '';
        const normalized = sourceText.replace(/\\/g, '/');
        const parts = normalized.split('/');
        const short = parts.length ? parts[parts.length - 1] || normalized : normalized;
        let label = sourceText;
        if (origin === 'overlay') {
            label = domain ? `db:${domain}` : `db:${short}`;
        } else {
            label = domain ? `${domain}:${short}` : short;
        }
        const titleParts = [];
        if (sourceText) {
            titleParts.push(sourceText);
        }
        if (domain) {
            titleParts.push(`domain: ${domain}`);
        }
        return {
            label,
            title: titleParts.join('\n') || sourceText,
            origin,
            domain
        };
    };
    const columns = [
        {
            Header: 'Ident',
            accessor: (f)=>f,
            Cell: ({ value })=>value == request.query.selected ? /*#__PURE__*/ _jsx("b", {
                    children: value
                }) : value,
            id: 'ident',
            filterMethod: filterMethod('ident'),
            filterAll: true
        }
    ];
    columns.push(...active_langs.map((lang)=>({
            Header: lang,
            accessor: (f)=>i18n_domain_raw[lang]?.[f] ?? i18n_raw[lang][f],
            id: lang,
            filterMethod: filterMethod(lang),
            filterAll: true,
            Cell: ({ value, original })=>/*#__PURE__*/ _jsx(EditableTranslationCell, {
                    lang: lang,
                    ident: original,
                    value: value,
                    onChange: handleOverlayChange,
                    savingKey: effectiveSavingKey,
                    sourceHint: getSourceHint(lang, original)
                })
        })));
    let idents = useMemo(()=>collectTranslationIdents(rowLangs, i18n_domain_raw, i18n_raw), [
        rowLangs,
        i18n_raw,
        i18n_domain_raw
    ]);
    idents = useMemo(()=>{
        if (overlayFilter == 'all') return idents;
        let f = (e)=>true;
        if (overlayFilter == 'db') {
            f = (hint)=>hint.origin == 'overlay';
        }
        if (overlayFilter == 'fs') {
            f = (hint)=>hint.origin == 'file';
        }
        if (overlayFilter == 'db_fs') {
            f = (hint)=>hint.origin == 'file' || hint.origin == 'overlay';
        }
        return idents.filter((e)=>f(getSourceHint(selectedLang, e) || {}));
    //~ return idents.filter(e => keys(i18n_raw).some(lang => !!getSourceHint(lang, e)?.origin == 'overlay') )
    }, [
        idents,
        selectedLang,
        overlayFilter
    ]);
    idents = useMemo(()=>{
        let sel = request.query.selected;
        if (!sel) return idents;
        return [
            sel,
            ...idents.filter((e)=>e != sel)
        ];
    }, [
        idents,
        request.query.selected
    ]);
    let langSwitchers = dconfig.language.supported.map((lang)=>/*#__PURE__*/ _jsx(XMenu.Item, {
            icon: !!i18n_domain_raw[lang] ? 'circle' : 'circle outline',
            title: `loaded: ${String(!!i18n_domain_raw[lang])}`,
            active: selectedLang == lang,
            onClick: ()=>switchLanguage(lang),
            content: i18n.get(`lang_${lang}`)
        }, lang));
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        size: "small",
        children: [
            overlayFilterButtonGroup,
            /*#__PURE__*/ _jsx(XMenu.Separator, {}),
            langSwitchers,
            showAllToggle
        ]
    });
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        header: `Translations (${dname})`,
        icon: "world",
        flex: flex,
        overflowInner: true,
        menu: menu,
        children: /*#__PURE__*/ _jsx(TranslatedTable, {
            flex: true,
            compact: true,
            showPagination: true,
            showPaginationTop: true,
            showPaginationBottom: false,
            filterable: true,
            className: "-striped -highlight dynHeight",
            data: idents,
            columns: columns,
            showCount: true
        })
    });
};
